// The scans you have already acted on this session, in the order you acted on them.
//
// This replaced a one-deep undo whose entry was assigned inside the background save's completion
// callback. Approves are fire-and-forget and run concurrently, so the undo slot was claimed by whichever
// upload *finished* last rather than whichever scan was approved last — and `save_item` uploads the
// primary image and then any reference frames one at a time, so completion order drifts from action
// order routinely. That is the whole reason undo kept offering the wrong item. Declines were worse: they
// recorded nothing at all, so a scan you declined could not be reached by any control.
//
// The fix is not a better guess about which one you meant. It is to stop guessing: every handled scan is
// recorded here **synchronously, at the moment the button is pressed**, and the approve screen's arrows
// walk that list. Nothing in this file may be called from a network callback, or the bug comes back.

// Re-acting on a scan updates it **where it already is** rather than adding a second row or moving it to
// the end. Approving something you had declined is the same scan with a different answer, not a new
// event to step through.
//
// Moving it to the end was tried first, on the argument that the answer you just gave is the most recent
// thing you did. That is true and it is not what this list is for: you are standing on an entry, reading
// it, and changing its state must not slide it out from under you — "3 of 5" turning into "5 of 5" loses
// your place in the walk and makes the arrows untrustworthy. The order is when you first handled each
// scan, which does not change.
export function appendHandled(history, entry) {
  if (!entry || !entry.tapId) return history;
  if (history.some((h) => h.tapId === entry.tapId)) return patchHandled(history, entry.tapId, entry);
  return [...history, entry];
}

// Used when the save comes back with the row it created. Keyed on tapId because the node id is exactly
// what is not known yet at the moment the entry is recorded.
export function patchHandled(history, tapId, patch) {
  if (!tapId || !patch) return history;
  return history.map((h) => (h.tapId === tapId ? { ...h, ...patch } : h));
}

export function findHandled(history, tapId) {
  return history.find((h) => h.tapId === tapId) || null;
}

// A scan stops being handled when its save fails and it goes back into the queue for a retry.
//
// The entry is recorded the instant Approve is pressed, before the network is touched — which is the whole
// point — so a failure has to take it back out. Leaving it would put one scan in two places at once: an
// error pill in the queue saying "tap to retry", and a history entry claiming it was approved with no row
// behind it, whose Decline button would silently do nothing because there is nothing to soft-delete.
export function removeHandled(history, tapId) {
  if (!tapId) return history;
  return history.filter((h) => h.tapId !== tapId);
}

// Where `<` and `>` land. `index` is null when the approve screen is showing a scan that has not been
// handled yet, and stepping back from there goes to the newest handled scan — the one you almost
// certainly mean, since it is the one you just acted on.
//
// Returns null to mean "leave the history and show the pending scan again", which is why this cannot
// simply be arithmetic at the call site: null is a position, not an absence.
export function stepHistory(history, index, delta) {
  if (!history.length) return null;
  const newest = history.length - 1;
  if (index === null || index === undefined) return delta < 0 ? newest : null;
  const next = index + delta;
  if (next < 0) return 0;            // Already at the oldest: stay rather than wrapping.
  if (next > newest) return null;    // Forward past the newest returns to the present.
  return next;
}

// "3 of 7", oldest first, so the numbers read the way a list does rather than counting backwards from
// whatever you happened to act on last.
export function historyPosition(history, index) {
  if (index === null || index === undefined || !history.length) return null;
  return { position: index + 1, total: history.length };
}

export function canStepBack(history, index) {
  if (!history.length) return false;
  return index === null || index === undefined ? true : index > 0;
}

// Forward always has somewhere to go while you are in the history: the next entry, or back out to the
// present. Only the present itself has nothing ahead of it.
export function canStepForward(history, index) {
  return !(index === null || index === undefined);
}
